import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Grid,
} from '@mui/material';
import { ArrowRight, Check } from 'lucide-react';
import ContactFormDialog from './ContactFormDialog';
import Reveal from './Reveal';
import CountUp from './CountUp';
import { tokens } from '../theme';

const miniStats = [
  { end: 73, prefix: '', suffix: '%', label: 'of leads never contacted' },
  { end: 30, prefix: '<', suffix: 's', label: 'avg CleoLead response time' },
  { end: 21, prefix: '', suffix: '×', label: 'more likely to qualify at 5 min' },
];

export default function CTASection() {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        bgcolor: tokens.bandAlt,
        borderTop: `1px solid ${tokens.border}`,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center' }}>
          <Reveal>
            <Typography variant="h2" sx={{ mb: 2 }}>
              Hire your AI Business Manager today
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', mb: 4, maxWidth: 520, mx: 'auto', fontSize: '1.1rem' }}
            >
              Every hour without CleoLead, your competitors are answering the call, replying to the DM, and booking the meeting you missed.
            </Typography>
          </Reveal>

          {/* mini stats — wraps on small screens */}
          <Reveal delay={70}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: { xs: 3, sm: 5 },
                mb: 5,
              }}
            >
              {miniStats.map((s) => (
                <Box key={s.label} sx={{ textAlign: 'center', maxWidth: 140 }}>
                  <Typography
                    sx={{
                      fontFamily: tokens.fontDisplay,
                      fontSize: '2rem',
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                      lineHeight: 1.1,
                    }}
                  >
                    <CountUp end={s.end} prefix={s.prefix} suffix={s.suffix} />
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: tokens.copper,
                      mt: 0.6,
                      lineHeight: 1.45,
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Reveal>

          <Reveal delay={140}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mb: 4 }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowRight size={18} strokeWidth={1.5} />}
                href="https://app.cleolead.com/login"
                sx={{
                  py: 1.6,
                  px: 4,
                  fontSize: '1.05rem',
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setContactOpen(true)}
                sx={{
                  py: 1.6,
                  px: 4,
                  fontSize: '1.05rem',
                }}
              >
                Talk to Sales
              </Button>
            </Stack>
          </Reveal>

          <ContactFormDialog
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            title="Talk to Sales"
            subtitle="Interested in CleoLead for your business? Fill out the form and our sales team will get back to you within 24 hours."
            formType="sales"
          />

          <Reveal delay={210}>
            <Grid container spacing={2} justifyContent="center">
              {['No credit card required', '14-day trial', '5-minute setup', 'Cancel anytime'].map(
                (benefit) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={benefit}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.82rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.7,
                      }}
                    >
                      <Check size={15} strokeWidth={1.5} color={tokens.sage} /> {benefit}
                    </Typography>
                  </Grid>
                ),
              )}
            </Grid>
          </Reveal>
        </Box>
      </Container>
    </Box>
  );
}
